import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../../../i18n';
import './TalkCallHistoryModal.css';

/**
 * TalkCallHistoryModal
 *
 * Two-pane modal that lists all past calls for a Talk conversation (left)
 * and shows the meeting-minutes / call summary for the selected call (right).
 *
 * @param {boolean}  isOpen
 * @param {function} onClose
 * @param {Array}    calls      - VideoMeeting records (with transcription eager-loaded)
 * @param {boolean}  isLoading
 * @param {Object}   conversation - current Talk conversation
 * @param {Object}   currentUser
 */
const TalkCallHistoryModal = ({
    isOpen,
    onClose,
    calls = [],
    isLoading = false,
    conversation,
    currentUser,
}) => {
    const { t } = useI18n();
    const [selectedCallId, setSelectedCallId] = useState(null);
    const [copied, setCopied] = useState(false);
    const overlayRef = useRef(null);

    // Select first call with minutes by default, else first call
    useEffect(() => {
        if (!isOpen || calls.length === 0) return;

        const withMinutes = calls.find(
            (c) =>
                c.transcription &&
                c.transcription.status === 'completed' &&
                c.transcription.content
        );
        setSelectedCallId((withMinutes ?? calls[0]).id);
    }, [isOpen, calls]);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const selectedCall = calls.find((c) => c.id === selectedCallId) ?? null;

    /* ── Helpers ──────────────────────────────────────────── */

    const formatDate = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const getInitials = (name) =>
        (name || '?')
            .split(' ')
            .slice(0, 2)
            .map((w) => w[0])
            .join('')
            .toUpperCase();

    const getAvatarColors = (name = '') => {
        const colors = [
            ['#3b82f6', '#6366f1'],
            ['#ec4899', '#f43f5e'],
            ['#10b981', '#14b8a6'],
            ['#f59e0b', '#f97316'],
            ['#8b5cf6', '#a855f7'],
        ];
        const idx = name.charCodeAt(0) % colors.length;
        return colors[idx];
    };

    /**
     * Determine the "other person" to display for this call.
     * For direct (one-to-one) calls → the other participant.
     * For group calls → host name.
     */
    const getCallDisplay = (call) => {
        if (conversation?.type !== 'team') {
            // Direct call — show the other user
            const otherUser = conversation?.users?.find(
                (u) => u.id !== currentUser?.id
            );
            return {
                name: otherUser?.name ?? call.title,
                avatar: otherUser?.avatar ?? null,
            };
        }
        // Group / team call — show the host
        return {
            name: call.host?.name ?? call.title,
            avatar: call.host?.avatar ?? null,
        };
    };

    /* ── Summary content renderer ─────────────────────────── */

    const renderSummaryContent = (call) => {
        const t = call?.transcription;

        if (!t || t.status !== 'completed') {
            const statusLabel = {
                pending: 'Meeting minutes are being prepared…',
                processing: 'Meeting minutes are being processed…',
                failed: 'Meeting minutes could not be generated for this call.',
                skipped: 'Meeting minutes were not available for this call (transcription not configured).',
            }[t?.status ?? ''];

            return (
                <div className="talk-history-no-minutes">
                    <span className="talk-history-no-minutes-icon">📝</span>
                    <p>{statusLabel ?? 'No meeting minutes available for this call.'}</p>
                </div>
            );
        }

        // content is { en: [...segments], ur: [...], ar: [...], fa: [...] }
        const content = t.content;
        const segments = Array.isArray(content?.en)
            ? content.en
            : Array.isArray(content)
                ? content
                : null;

        if (!segments || segments.length === 0) {
            return (
                <div className="talk-history-no-minutes">
                    <span className="talk-history-no-minutes-icon">📋</span>
                    <p>The call summary is empty.</p>
                </div>
            );
        }

        // If segments have text property, render them
        if (typeof segments[0] === 'object' && segments[0].text) {
            return segments.map((seg, i) => (
                <div key={i} className="talk-history-segment">
                    {seg.user_name && (
                        <div className="talk-history-segment-speaker">
                            {seg.user_name}
                        </div>
                    )}
                    <div className="talk-history-segment-text">{seg.text}</div>
                </div>
            ));
        }

        // Plain strings
        return (
            <div className="talk-history-summary-paragraph">
                {segments.join('\n\n')}
            </div>
        );
    };

    /* ── Copy handler ─────────────────────────────────────── */

    const handleCopy = () => {
        const t = selectedCall?.transcription;
        if (!t?.content) return;

        const segments = Array.isArray(t.content?.en)
            ? t.content.en
            : Array.isArray(t.content)
                ? t.content
                : [];

        const text = segments
            .map((s) =>
                typeof s === 'object'
                    ? (s.user_name ? `${s.user_name}: ${s.text}` : s.text)
                    : s
            )
            .join('\n\n');

        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    /* ── Backdrop click ───────────────────────────────────── */

    const handleOverlayClick = (e) => {
        if (e.target === overlayRef.current) onClose();
    };

    /* ── Render ───────────────────────────────────────────── */

    return (
        <div
            className="talk-history-overlay"
            ref={overlayRef}
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-label="Call History"
        >
            <div className="talk-history-modal">

                {/* Header */}
                <div className="talk-history-header">
                    <h2 className="talk-history-title">
                        {conversation?.type === 'team' ? t('talk.groupCallHistory', {}, 'Group Call History') : t('talk.videoCallsHistory', {}, 'Video Calls History')}
                    </h2>
                    <button
                        className="talk-history-close"
                        onClick={onClose}
                        aria-label={t('talk.close', {}, 'Close')}
                    >
                        ×
                    </button>
                </div>

                {/* Body */}
                <div className="talk-history-body">

                    {/* Left pane — call list */}
                    <div className="talk-history-list-pane">
                        {isLoading ? (
                            <div className="talk-history-loading">
                                <div className="talk-history-spinner" />
                                <span>{t('talk.loadingHistory', {}, 'Loading call history…')}</span>
                            </div>
                        ) : calls.length === 0 ? (
                            <div className="talk-history-list-empty">
                                <span className="talk-history-list-empty-icon">📞</span>
                                <p>{t('talk.noCallsYet', {}, 'No calls yet in this conversation.')}</p>
                            </div>
                        ) : (
                            calls.map((call) => {
                                const display = getCallDisplay(call);
                                const colors = getAvatarColors(display.name);
                                const isSelected = call.id === selectedCallId;
                                const hasMinutes =
                                    call.transcription?.status === 'completed' &&
                                    call.transcription?.content;
                                const direction = call.call_direction ?? 'outgoing';

                                return (
                                    <div
                                        key={call.id}
                                        className={`talk-history-call-item${isSelected ? ' selected' : ''}`}
                                        onClick={() => setSelectedCallId(call.id)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setSelectedCallId(call.id);
                                            }
                                        }}
                                    >
                                        {/* Avatar */}
                                        <div
                                            className="talk-history-avatar"
                                            style={{
                                                background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
                                            }}
                                        >
                                            {display.avatar ? (
                                                <img src={display.avatar} alt={display.name} />
                                            ) : (
                                                getInitials(display.name)
                                            )}
                                        </div>

                                        {/* Info */}
                                        <div className="talk-history-call-info">
                                            <div className="talk-history-call-name">
                                                {display.name}
                                            </div>
                                            <div className="talk-history-call-meta">
                                                {/* Direction icon */}
                                                <span
                                                    className={`talk-history-direction-icon ${direction}`}
                                                    title={direction === 'incoming' ? t('talk.incoming', {}, 'Incoming') : t('talk.outgoing', {}, 'Outgoing')}
                                                >
                                                    {direction === 'incoming' ? (
                                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M10.125 7.31169V4.49919C10.125 4.35001 10.1843 4.20693 10.2898 4.10144C10.3952 3.99595 10.5383 3.93669 10.6875 3.93669C10.8367 3.93669 10.9798 3.99595 11.0853 4.10144C11.1907 4.20693 11.25 4.35001 11.25 4.49919V5.95396L13.6645 3.53872C13.7701 3.43317 13.9132 3.37388 14.0625 3.37388C14.2118 3.37388 14.3549 3.43317 14.4605 3.53872C14.566 3.64427 14.6253 3.78742 14.6253 3.93669C14.6253 4.08596 14.566 4.22911 14.4605 4.33466L12.0459 6.74919H13.5C13.6492 6.74919 13.7923 6.80845 13.8978 6.91394C14.0032 7.01943 14.0625 7.16251 14.0625 7.31169C14.0625 7.46087 14.0032 7.60395 13.8978 7.70944C13.7923 7.81493 13.6492 7.87419 13.5 7.87419H10.6875C10.5383 7.87419 10.3952 7.81493 10.2898 7.70944C10.1843 7.60395 10.125 7.46087 10.125 7.31169ZM15.7416 12.872C15.6162 13.8248 15.1483 14.6993 14.4253 15.3323C13.7022 15.9653 12.7735 16.3134 11.8125 16.3117C6.22969 16.3117 1.68751 11.7695 1.68751 6.18669C1.68566 5.22576 2.03377 4.29703 2.6668 3.57408C3.29982 2.85112 4.17444 2.3834 5.12719 2.25833C5.36804 2.22886 5.61196 2.27805 5.82256 2.39855C6.03317 2.51906 6.19916 2.70443 6.29579 2.927L7.78079 6.24224V6.25067C7.85468 6.42115 7.88519 6.60727 7.86961 6.79242C7.85403 6.97756 7.79283 7.15596 7.69149 7.31169C7.67884 7.33067 7.66548 7.34825 7.65141 7.36583L6.18751 9.10114C6.71415 10.1713 7.83352 11.2808 8.91774 11.8089L10.6291 10.3527C10.646 10.3386 10.6636 10.3254 10.6819 10.3133C10.8375 10.2096 11.0165 10.1462 11.2027 10.129C11.389 10.1118 11.5766 10.1413 11.7485 10.2149L11.7577 10.2191L15.0701 11.7034C15.2932 11.7996 15.4792 11.9654 15.6002 12.176C15.7212 12.3867 15.7708 12.6308 15.7416 12.872ZM14.625 12.7314C14.625 12.7314 14.6201 12.7314 14.6173 12.7314L11.3126 11.2513L9.60048 12.7075C9.58404 12.7218 9.56666 12.7349 9.54845 12.7468C9.38655 12.8549 9.19942 12.919 9.00531 12.9331C8.81121 12.9471 8.61678 12.9106 8.44102 12.827C7.12407 12.1907 5.81134 10.8878 5.1743 9.58489C5.08995 9.41042 5.0521 9.21711 5.06442 9.0237C5.07673 8.83029 5.13881 8.64335 5.24462 8.48099C5.25654 8.46192 5.26994 8.44382 5.28469 8.42685L6.75001 6.68942L5.27344 3.38474C5.27317 3.38193 5.27317 3.3791 5.27344 3.3763C4.59159 3.46524 3.96555 3.7997 3.51255 4.31702C3.05955 4.83435 2.81066 5.49906 2.81251 6.18669C2.81511 8.57284 3.76416 10.8605 5.45142 12.5478C7.13869 14.235 9.42636 15.1841 11.8125 15.1867C12.4997 15.1891 13.1643 14.941 13.6819 14.489C14.1995 14.037 14.5348 13.4119 14.625 12.7307V12.7314Z" fill="#343330" />
                                                        </svg>
                                                    ) : (
                                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M10.2895 7.71036C10.2372 7.65812 10.1957 7.59608 10.1674 7.5278C10.1391 7.45951 10.1246 7.38631 10.1246 7.31239C10.1246 7.23847 10.1391 7.16528 10.1674 7.09699C10.1957 7.0287 10.2372 6.96667 10.2895 6.91442L12.7048 4.49989H11.25C11.1008 4.49989 10.9577 4.44063 10.8523 4.33514C10.7468 4.22965 10.6875 4.08658 10.6875 3.93739C10.6875 3.78821 10.7468 3.64513 10.8523 3.53965C10.9577 3.43416 11.1008 3.37489 11.25 3.37489H14.0625C14.2117 3.37489 14.3548 3.43416 14.4603 3.53965C14.5657 3.64513 14.625 3.78821 14.625 3.93739V6.74989C14.625 6.89908 14.5657 7.04215 14.4603 7.14764C14.3548 7.25313 14.2117 7.31239 14.0625 7.31239C13.9133 7.31239 13.7702 7.25313 13.6648 7.14764C13.5593 7.04215 13.5 6.89908 13.5 6.74989V5.29513L11.0855 7.71036C11.0332 7.76266 10.9712 7.80415 10.9029 7.83246C10.8346 7.86077 10.7614 7.87534 10.6875 7.87534C10.6136 7.87534 10.5404 7.86077 10.4721 7.83246C10.4038 7.80415 10.3418 7.76266 10.2895 7.71036ZM15.7416 12.8727C15.6162 13.8255 15.1483 14.7 14.4253 15.333C13.7022 15.966 12.7735 16.3141 11.8125 16.3124C6.22969 16.3124 1.68751 11.7702 1.68751 6.18739C1.68578 5.22643 2.03393 4.29769 2.66691 3.57465C3.2999 2.85161 4.17444 2.3837 5.12719 2.25833C5.36804 2.22886 5.61196 2.27805 5.82256 2.39855C6.03317 2.51906 6.19916 2.70443 6.29579 2.927L7.78079 6.24294V6.25138C7.85468 6.42185 7.88519 6.60797 7.86961 6.79312C7.85403 6.97826 7.79283 7.15667 7.69149 7.31239C7.67883 7.33138 7.66548 7.34896 7.65141 7.36653L6.18751 9.10185C6.71415 10.172 7.83352 11.2815 8.91774 11.8096L10.6291 10.3534C10.646 10.3393 10.6636 10.3261 10.6819 10.314C10.8373 10.2098 11.0163 10.1462 11.2027 10.129C11.389 10.1118 11.5767 10.1416 11.7485 10.2156L11.7577 10.2198L15.0701 11.7041C15.2932 11.8003 15.4792 11.9661 15.6002 12.1767C15.7212 12.3874 15.7708 12.6315 15.7416 12.8727ZM14.625 12.7321C14.625 12.7321 14.6201 12.7321 14.6173 12.7321L11.3126 11.2513L9.60047 12.7082C9.58387 12.7222 9.5665 12.7354 9.54844 12.7475C9.38655 12.8556 9.19941 12.9197 9.00531 12.9338C8.81121 12.9478 8.61678 12.9113 8.44102 12.8277C7.12407 12.1914 5.81133 10.8885 5.1743 9.5856C5.08995 9.41112 5.0521 9.21781 5.06442 9.0244C5.07673 8.831 5.13881 8.64405 5.24462 8.48169C5.25654 8.46263 5.26994 8.44452 5.28469 8.42755L6.75001 6.69013L5.27344 3.38544C5.27317 3.38263 5.27317 3.37981 5.27344 3.377C4.59159 3.46595 3.96555 3.8004 3.51255 4.31772C3.05955 4.83505 2.81066 5.49977 2.81251 6.18739C2.81511 8.57354 3.76416 10.8612 5.45142 12.5485C7.13869 14.2357 9.42636 15.1874C11.8125 15.1874C12.4997 15.1898 13.1642 14.9417 13.6819 14.4897C14.1995 14.0377 14.5348 13.4126 14.625 12.7314V12.7321Z" fill="#343330" />
                                                        </svg>
                                                    )}
                                                </span>
                                                <span>
                                                    {direction === 'incoming' ? t('talk.incoming', {}, 'Incoming') : t('talk.outgoing', {}, 'Outgoing')}
                                                </span>
                                                {call.call_type === 'voice' && (
                                                    <span>· {t('talk.voice', {}, 'Voice')}</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Date */}
                                        <div className="talk-history-call-date">
                                            {formatDate(call.created_at)}
                                        </div>

                                        {/* Green dot if minutes available */}
                                        {hasMinutes && (
                                            <div
                                                className="talk-history-has-minutes-badge"
                                                title={t('talk.summaryAvailable', {}, 'Call summary available')}
                                            />
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Right pane — summary */}
                    <div className="talk-history-summary-pane">
                        {!selectedCall ? (
                            <div className="talk-history-summary-empty">
                                <span className="talk-history-summary-empty-icon">👈</span>
                                <p>{t('talk.selectCallForSummary', {}, 'Select a call from the list to view its summary.')}</p>
                            </div>
                        ) : (
                            <>
                                {/* Summary header */}
                                <div className="talk-history-summary-header">
                                    <div className="talk-history-summary-heading">
                                        <span className="talk-history-summary-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M17.25 10.5C17.25 10.6989 17.171 10.8897 17.0303 11.0303C16.8897 11.171 16.6989 11.25 16.5 11.25H10.5C10.3011 11.25 10.1103 11.171 9.96967 11.0303C9.82902 10.8897 9.75 10.6989 9.75 10.5C9.75 10.3011 9.82902 10.1103 9.96967 9.96967C10.1103 9.82902 10.3011 9.75 10.5 9.75H16.5C16.6989 9.75 16.8897 9.82902 17.0303 9.96967C17.171 10.1103 17.25 10.3011 17.25 10.5ZM16.5 12.75H10.5C10.3011 12.75 10.1103 12.829 9.96967 12.9697C9.82902 13.1103 9.75 13.3011 9.75 13.5C9.75 13.6989 9.82902 13.8897 9.96967 14.0303C10.1103 14.171 10.3011 14.25 10.5 14.25H16.5C16.6989 14.25 16.8897 14.171 17.0303 14.0303C17.171 13.8897 17.25 13.6989 17.25 13.5C17.25 13.3011 17.171 13.1103 17.0303 12.9697C16.8897 12.829 16.6989 12.75 16.5 12.75ZM21 4.5V19.5C21 19.8978 20.842 20.2794 20.5607 20.5607C20.2794 20.842 19.8978 21 19.5 21H4.5C4.10218 21 3.72064 20.842 3.43934 20.5607C3.15804 20.2794 3 19.8978 3 19.5V4.5C3 4.10218 3.15804 3.72064 3.43934 3.43934C3.72064 3.15804 4.10218 3 4.5 3H19.5C19.8978 3 20.2794 3.15804 20.5607 3.43934C20.842 3.72064 21 4.10218 21 4.5ZM4.5 19.5H6.75V4.5H4.5V19.5ZM19.5 19.5V4.5H8.25V19.5H19.5Z" fill="#0C5097" />
                                            </svg>
                                        </span>
                                        <span>{t('talk.callSummary', {}, 'Call Summary')}</span>
                                    </div>
                                    {selectedCall.transcription?.status === 'completed' &&
                                        selectedCall.transcription?.content && (
                                            <button
                                                className={`talk-history-copy-btn${copied ? ' copied' : ''}`}
                                                onClick={handleCopy}
                                            >
                                                {copied ? (
                                                    <>
                                                        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#10b981" />
                                                        </svg>
                                                        <span>{t('talk.copied', {}, 'Copied')}</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M12.7656 12.1717C12.7656 12.4079 12.6718 12.6345 12.5048 12.8015C12.3377 12.9685 12.1112 13.0623 11.875 13.0623H7.125C6.88879 13.0623 6.66226 12.9685 6.49523 12.8015C6.32821 12.6345 6.23438 12.4079 6.23438 12.1717C6.23438 11.9355 6.32821 11.709 6.49523 11.5419C6.66226 11.3749 6.88879 11.2811 7.125 11.2811H11.875C12.1112 11.2811 12.3377 11.3749 12.5048 11.5419C12.6718 11.709 12.7656 11.9355 12.7656 12.1717ZM11.875 8.31234H7.125C6.88879 8.31234 6.66226 8.40617 6.49523 8.57319C6.32821 8.74022 6.23438 8.96675 6.23438 9.20296C6.23438 9.43917 6.32821 9.6657 6.49523 9.83273C6.66226 9.99975 6.88879 10.0936 7.125 10.0936H11.875C12.1112 10.0936 12.3377 9.99975 12.5048 9.83273C12.6718 9.6657 12.7656 9.43917 12.7656 9.20296C12.7656 8.96675 12.6718 8.74022 12.5048 8.57319C12.3377 8.40617 12.1112 8.31234 11.875 8.31234ZM16.3281 3.56234V16.0311C16.3281 16.4248 16.1717 16.8023 15.8934 17.0807C15.615 17.3591 15.2374 17.5155 14.8438 17.5155H4.15625C3.76257 17.5155 3.38501 17.3591 3.10664 17.0807C2.82826 16.8023 2.67188 16.4248 2.67188 16.0311V3.56234C2.67188 3.16865 2.82826 2.7911 3.10664 2.51272C3.38501 2.23435 3.76257 2.07796 4.15625 2.07796H6.71902C7.07845 1.70263 7.51014 1.40393 7.98808 1.19988C8.46602 0.995822 8.98032 0.890625 9.5 0.890625C10.0197 0.890625 10.534 0.995822 11.0119 1.19988C11.4899 1.40393 11.9215 1.70263 12.281 2.07796H14.8438C15.2374 2.07796 15.615 2.23435 15.8934 2.51272C16.1717 2.7911 16.3281 3.16865 16.3281 3.56234ZM7.4434 4.45296H11.5566C11.4849 3.95867 11.2376 3.50673 10.8599 3.17989C10.4822 2.85304 9.99947 2.67315 9.5 2.67315C9.00053 2.67315 8.51777 2.85304 8.1401 3.17989C7.76242 3.50673 7.5151 3.95867 7.4434 4.45296ZM14.5469 3.85921H13.2547C13.324 4.15103 13.3591 4.44991 13.3594 4.74984V5.34359C13.3594 5.57979 13.2655 5.80633 13.0985 5.97335C12.9315 6.14038 12.705 6.23421 12.4688 6.23421H6.53125C6.29504 6.23421 6.06851 6.14038 5.90148 5.97335C5.73446 5.80633 5.64063 5.57979 5.64062 5.34359V4.74984C5.64087 4.44991 5.67599 4.15103 5.74527 3.85921H4.45312V15.7342H14.5469V3.85921Z" fill="#0C5097" />
                                                        </svg>
                                                        <span>{t('talk.copy', {}, 'Copy')}</span>
                                                    </>
                                                )}
                                            </button>
                                        )}
                                </div>


                                {/* Scrollable summary body */}
                                <div className="talk-history-summary-content">
                                    {renderSummaryContent(selectedCall)}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TalkCallHistoryModal;
